import App from '@app/app';
import UpgradeType from '@app/store/UpgradeType.js';
import { OrderLineDetail } from '@app/service/OrderLineFactory';
import '@app/service/common/PersistentCartPreprocessorService';

export class PersistentCartService {
  constructor(
    $http,
    $q,
    $window,
    $injector,
    JobStore,
    SubjectStore,
    OrderLineFactory,
    LooksStore,
    ProductCatalogStore,
    ItemService,
    EventDataService,
    ErrorLogService,
    StatsdService,
    StorageService,
    DeserializeAction,
    SessionTimeoutAction,
    LogoutSuccessAction,
    PersistentCartPreprocessorService,
    EnhancementStore
  ) {
    this.$http = $http;
    this.$q = $q;
    this.$window = $window;
    this.$injector = $injector;
    this.jobStore = JobStore;
    this.subjectStore = SubjectStore;
    this.orderLineFactory = OrderLineFactory;
    this.looksStore = LooksStore;
    this.productCatalogStore = ProductCatalogStore;
    this.itemService = ItemService;
    this.eventDataService = EventDataService;
    this.errorLogService = ErrorLogService;
    this.statsdService = StatsdService;
    this.storageService = StorageService;
    this.persistentCartPreprocessorService = PersistentCartPreprocessorService;
    this.enhancementStore = EnhancementStore;
    this.shoppingCartStore = null;

    this.persistentCartMetadata = null;
    this.incompleteRestore = false;

    DeserializeAction.register(this._onDeserialized.bind(this));
    SessionTimeoutAction.register(this._clearAll.bind(this));
    LogoutSuccessAction.register(this._clearAll.bind(this));
  }

  load() {
    this.shoppingCartStore = this.getShoppingCartStore();
    if (this._shouldSkipRestore()) {
      return this.$q.resolve();
    }

    return this._fetchPersistentCart()
      .then((persistentCart) => this._restore(persistentCart))
      .then(() => {
        this.statsdService.incrementCounter('PERSISTENT_CART_LOAD_SUCCESS');
      })
      .catch(() => {
        this.statsdService.incrementCounter('PERSISTENT_CART_LOAD_FAILURE');
      });
  }

  delete() {
    return this._isEnabled() ? this.$http.post('persistentCart/delete').catch(() => {}) : this.$q.resolve();
  }

  _shouldSkipRestore() {
    return !this._isEnabled() || this._isRestored();
  }

  _isEnabled() {
    return this.$window.persistentCartEnabledFlag;
  }

  _isRestored() {
    return Boolean(this.persistentCartMetadata);
  }

  _fetchPersistentCart() {
    return this.$http
      .get('persistentCart/load')
      .then(({ data }) => data.data)
      .catch(() => this.errorLogService(new Error('Error loading persistent cart')));
  }

  async _restore(persistentCart) {
    this._storePersistentCartMetadata(persistentCart || {});

    if (!persistentCart) return;

    const currentSubject = this.shoppingCartStore.getCurrentSubject();
    const persistedOrders = this.persistentCartPreprocessorService.preprocess(persistentCart.orders);

    // TODO: sequential because legacy logic uses global state and /preorder & /reorder are not threadsafe
    for (const persistedOrder of persistedOrders) {
      await this._restoreOrder(persistedOrder);
      this._restoreEnhancementStore(persistedOrder);
    }

    this._updateStorage();

    await this.shoppingCartStore.repriceCart();
    await this._restoreCoupons(persistedOrders);

    if (currentSubject) {
      this.shoppingCartStore.changeCurrentSubject(currentSubject);

      await this.getAddVerifiedSubjectService().selectSubject();
    }
  }

  _storePersistentCartMetadata(persistentCart) {
    this.persistentCartMetadata = {
      cartToken: persistentCart.cartToken,
      dateCreated: persistentCart.dateCreated,
      lastUpdated: persistentCart.lastUpdated
    };

    this._updateStorage();
  }

  _updateStorage() {
    this.storageService.setData('persistentCart', {
      persistentCartMetadata: this.persistentCartMetadata,
      incompleteRestore: this.incompleteRestore
    });
  }

  _restoreEnhancementStore(persistentOrder) {
    const currentSubject = this.shoppingCartStore.getCurrentSubject();

    const upgrades = this._extractUpgradesFromOrder(persistentOrder);

    this._restorePersonalization(upgrades, currentSubject);
    this._restoreRetouch(upgrades, currentSubject);
  }

  _extractUpgradesFromOrder(persistentOrder) {
    return [
      ...persistentOrder.upgrades.map((upgrade) => upgrade.orderable),
      ...persistentOrder.packages.map((pkg) => pkg.orderable.upgrades)
    ].flat();
  }

  _restorePersonalization(upgrades, currentSubject) {
    const personalization = upgrades.find((upg) => upg.type === UpgradeType.PERSONALIZATION);
    const fallbackName = this.subjectStore.isGeneratedFirstNamePlaceholder(currentSubject.subjectFirstName)
      ? ''
      : currentSubject.subjectFirstName;

    this.enhancementStore.setPersonalizationName({
      subjectId: currentSubject.id,
      value: {
        value: personalization?.orderLineItemSelections?.[0]?.value || fallbackName,
        skuCode: personalization?.skuCode || null
      }
    });
  }

  _restoreRetouch(upgrades, currentSubject) {
    const retouching = upgrades.find((upg) => upg.type.includes('RETOUCHING'));
    this.enhancementStore.setRetouch({
      subjectId: currentSubject.id,
      value: retouching?.type || null
    });
  }

  _onDeserialized(type, data) {
    if (data.persistentCart) {
      this.persistentCartMetadata = data.persistentCart.persistentCartMetadata;
      this.incompleteRestore = data.persistentCart.incompleteRestore;
    }
  }

  _clearAll() {
    this.persistentCartMetadata = null;
    this.incompleteRestore = false;

    this._updateStorage();
  }

  async _restoreOrder(persistedOrder) {
    await this._loadSubject(persistedOrder);

    this._restorePackages(persistedOrder);
    this._restoreAddOns(persistedOrder);
    this._restoreBundles(persistedOrder);
    this._restoreUpgrades(persistedOrder);
  }

  async _loadSubject(persistedOrder) {
    await this._loadJob(persistedOrder);

    await this.getAddVerifiedSubjectService().selectSubject();
  }

  async _loadJob(persistedOrder) {
    await this.jobStore.loadJob(this._getPictureDayData(persistedOrder), false);

    const subject = this.shoppingCartStore.getCurrentSubject();

    if (!persistedOrder.accessCredential) {
      subject.subjectFirstName =
        persistedOrder.subjectFirstName ??
        (persistedOrder.subjectDetailsDeferred ? this.subjectStore.generateFirstNamePlaceholder() : null);
      subject.subjectLastName = persistedOrder.subjectLastName;
      subject.subjectGrade = persistedOrder.subjectGrade;
      subject.subjectRelationship = persistedOrder.subjectRelationship;
      // Always mark a subject restored from a persisted cart as deferred so the student-details
      // modal re-opens at checkout and the customer re-confirms their details before paying.
      subject.subjectDetailsDeferred = true;
      subject.allowPersonalizationNamePrefill = Boolean(
        subject.subjectFirstName && !this.subjectStore.isGeneratedFirstNamePlaceholder(subject.subjectFirstName)
      );
    }
  }

  _getPictureDayData(order) {
    const accessCredential = order.accessCredential;

    if (accessCredential) {
      return {
        ...this.eventDataService.parseAccessCredential(accessCredential),
        subjectLastName: order.subjectLastName,
        childGrade: order.subjectGrade
      };
    } else {
      return {
        pictureDayId: order.jobNumber
      };
    }
  }

  _restorePackages(persistedOrder) {
    persistedOrder.packages
      .map((persistedPackage) =>
        persistedPackage.orderable.substitutable
          ? this._restoreCyopPackage(persistedPackage)
          : this._restorePackage(persistedPackage)
      )
      .filter((restoredPackage) => restoredPackage != null)
      .forEach((restoredPackage) => {
        this.shoppingCartStore.addOrUpdatePackage(
          restoredPackage, // package
          [], // upgrades
          -1, // index
          null, // orderIndex
          true, // nameChanged
          [], // favouriteLooks
          false // repriceCart
        );
      });
  }

  _restorePackage(persistedPackage) {
    const skuCode = persistedPackage.orderable.skuCode;

    const catalogPackage = this.productCatalogStore.findPackageBySku(skuCode);

    if (!catalogPackage) {
      this._markIncomplete(`Package with SKU not found in catalog: ${skuCode}`);

      return null;
    }

    const restoredOrderLine = this.orderLineFactory.createFromPackage(catalogPackage);

    this._restoreOrderLineDetails(restoredOrderLine, persistedPackage);

    return restoredOrderLine;
  }

  _restoreCyopPackage(persistedCyopPackage) {
    const skuCode = persistedCyopPackage.orderable.skuCode;

    const catalogCyopPackage = this.productCatalogStore.findPackageBySku(skuCode);

    if (!catalogCyopPackage) {
      this._markIncomplete(`Package with SKU not found in catalog: ${skuCode}`);

      return null;
    }

    const restoredOrderLineDetails = this._restoreCyopOrderLineDetails(persistedCyopPackage);

    if (!restoredOrderLineDetails) {
      this._markIncomplete(`Unable to restore CYOP addOns: ${skuCode}`);

      return null;
    }

    const restoredOrderLine = this.orderLineFactory.createFromCyop(catalogCyopPackage, {
      orderLineDetails: restoredOrderLineDetails
    });

    this._restoreOrderLineDetails(restoredOrderLine, persistedCyopPackage);

    return restoredOrderLine;
  }

  _restoreCyopOrderLineDetails(persistedCyopPackage) {
    const catalogCyopAddOns = persistedCyopPackage.orderLineDetails.map((persistedOrderLineDetail) =>
      this.productCatalogStore.findAddOnBySku(persistedOrderLineDetail.bundledProductDTO.skuCode)
    );

    const restorable = catalogCyopAddOns.every((catalogCyopAddOn) => catalogCyopAddOn != null);

    if (!restorable) {
      return null;
    }

    return catalogCyopAddOns
      .map((catalogCyopAddOn) => R.clone(catalogCyopAddOn))
      .map((catalogCyopAddOn) =>
        catalogCyopAddOn.lookSelectionAllowed
          ? new OrderLineDetail(catalogCyopAddOn, this.looksStore.getDefaultLook())
          : new OrderLineDetail(catalogCyopAddOn)
      );
  }

  _restoreAddOns(persistedOrder) {
    const restoredAddOns = persistedOrder.addOns
      .map((persistedAddOn) =>
        this._isPayOnlyAddOn(persistedAddOn)
          ? this._restorePayOnlyAddOn(persistedAddOn)
          : this._restoreAddOn(persistedAddOn)
      )
      .filter((restoredAddOn) => restoredAddOn != null);

    if (restoredAddOns.length) {
      this.shoppingCartStore.addOrUpdateAddOns(
        restoredAddOns, // addOns
        [], // upgrades
        -1, // index
        null, // orderIndex
        false // repriceCart
      );
    }
  }

  _restoreAddOn(persistedAddOn) {
    const skuCode = persistedAddOn.orderable.skuCode;

    const catalogAddOn = this.productCatalogStore.findAddOnBySku(skuCode);

    if (!catalogAddOn) {
      this._markIncomplete(`AddOn with SKU not found in catalog: ${skuCode}`);

      return null;
    }

    const restoredOrderLine = this.orderLineFactory.createFromAddOn(catalogAddOn, this.looksStore.getDefaultLook());

    this._restoreOrderLineDetails(restoredOrderLine, persistedAddOn);

    return restoredOrderLine;
  }

  _restorePayOnlyAddOn(persistedPayOnlyAddOn) {
    const catalogAddOn = this.productCatalogStore.getPayOnlyAddOn(persistedPayOnlyAddOn.orderable.unitPrice);

    const restoredOrderLine = this.orderLineFactory.createFromAddOn(catalogAddOn);

    this._restoreOrderLineDetails(restoredOrderLine, persistedPayOnlyAddOn);

    return restoredOrderLine;
  }

  _restoreBundles(persistedOrder) {
    persistedOrder.bundles
      .map((persistedBundle) => this._restoreBundle(persistedBundle))
      .filter((restoredBundle) => restoredBundle != null)
      .forEach((restoredBundle) => {
        this.shoppingCartStore.addOrUpdateBundle(
          restoredBundle, // bundle
          [], // upgrades
          -1, // index
          null, // orderIndex
          false // repriceCart
        );
      });
  }

  _restoreBundle(persistedBundle) {
    const skuCode = persistedBundle.orderable.skuCode;

    const catalogBundle = this.productCatalogStore.findBundleBySku(skuCode);

    if (!catalogBundle) {
      this._markIncomplete(`Bundle with SKU not found in catalog: ${skuCode}`);

      return null;
    }

    const restoredOrderLine = this.orderLineFactory.createFromBundle(catalogBundle);

    this._restoreOrderLineDetails(restoredOrderLine, persistedBundle);

    return restoredOrderLine;
  }

  _restoreUpgrades(persistedOrder) {
    const restoredUpgrades = persistedOrder.upgrades
      .map((persistedUpgrade) => this._restoreUpgrade(persistedUpgrade))
      .filter((restoredUpgrade) => restoredUpgrade != null)
      .map((restoredUpgrade) => ({
        isRetouching: this._isRetouchingUpgrade(restoredUpgrade),
        orderLine: restoredUpgrade
      }));

    if (restoredUpgrades.length) {
      this.shoppingCartStore.updateUpgrades(
        restoredUpgrades, // upgrades
        false, // repriceCart
        null // orderIndex
      );
    }
  }

  _restoreUpgrade(persistedUpgrade) {
    const skuCode = persistedUpgrade.orderable.skuCode;

    const catalogUpgrade = this.productCatalogStore.findUpgradeBySku(skuCode);

    if (!catalogUpgrade) {
      this._markIncomplete(`Upgrade with SKU not found in catalog: ${skuCode}`);

      return null;
    }

    const restoredOrderLine = this.orderLineFactory.createFromEnhancement(catalogUpgrade);

    /*
      Unexpected: persistedUpgrade.orderLineDetails is empty from backend;
      reusing data from orderable instead
     */
    this._restoreOrderLineDetail(restoredOrderLine, {
      item: persistedUpgrade.orderable
    });

    return restoredOrderLine;
  }

  _restoreOrderLineDetails(restoredOrderLine, persistedProduct) {
    persistedProduct.orderLineDetails.forEach((persistedOrderLineDetail) => {
      this._restoreOrderLineDetail(restoredOrderLine, persistedOrderLineDetail);
    });
  }

  _restoreOrderLineDetail(restoredOrderLine, persistedOrderLineDetail) {
    if (!persistedOrderLineDetail.item) {
      persistedOrderLineDetail.item = persistedOrderLineDetail.bundledProductDTO;
    }

    const persistedLookNumber = persistedOrderLineDetail.lookNumber;
    const persistedItem = persistedOrderLineDetail.item;
    const restoredOrderLineDetail = restoredOrderLine.orderLineDetails.find(
      (orderLineDetail) => orderLineDetail.item.skuCode === persistedItem.skuCode && !orderLineDetail.restored
    );

    if (!restoredOrderLineDetail) {
      this._markIncomplete(`No matching item found for SKU ${persistedItem.skuCode}`);

      return;
    }

    restoredOrderLineDetail.restored = true;

    const restoredItem = restoredOrderLineDetail.item;

    this.itemService.loadParsedSheets(restoredOrderLineDetail, restoredOrderLine.orderable.substitutable);

    if (typeof persistedLookNumber === 'string') {
      const restoredLook = this.looksStore.getLookByLookNumber(persistedLookNumber);

      if (!restoredLook) {
        if (restoredItem.lookSelectionAllowed) {
          this._markIncomplete(`No look found for persistedLookNumber: ${persistedLookNumber}`);
        } else {
          // no logging needed: this is a valid case (e.g. lookNumber = "D")
        }

        return;
      }

      restoredOrderLineDetail.look = restoredLook;
    } else if (restoredItem.type === UpgradeType.PERSONALIZATION) {
      restoredItem.orderLineItemSelections = persistedItem.orderLineItemSelections;
    } else if (this._isRetouchingItem(restoredItem)) {
      // do nothing
    } else if (this._isPayOnlyAddOn(restoredOrderLine)) {
      // do nothing
    } else {
      this._markIncomplete(`No handler implemented for restoring this item type: ${restoredItem}`);
    }
  }

  _markIncomplete(warn) {
    this.incompleteRestore = true;

    console.warn(warn);
  }

  _isRetouchingUpgrade(upgrade) {
    return this._isRetouchingItem(upgrade.orderLineDetails[0].item);
  }

  _isRetouchingItem(item) {
    return item.type === UpgradeType.PREMIUM_RETOUCHING || item.type === UpgradeType.BASIC_RETOUCHING;
  }

  _isPayOnlyAddOn(persistedAddOn) {
    return persistedAddOn.orderable.skuCode === this.productCatalogStore.getPayOnlySkuCode();
  }

  async _restoreCoupons(persistedOrders) {
    for (const persistedCouponCode of this._getPersistedCouponCodes(persistedOrders)) {
      await this.shoppingCartStore.applyCoupon(persistedCouponCode);
    }
  }

  _getPersistedCouponCodes(persistedOrders) {
    const couponCodes = persistedOrders
      .flatMap((persistedOrder) => this._getPersistedOrderPromotions(persistedOrder))
      .map((promotion) => promotion.couponCode);

    return [...new Set(couponCodes)];
  }

  _getPersistedOrderPromotions(persistedOrder) {
    return [
      ...persistedOrder.appliedOrderPromotions,
      ...persistedOrder.orderLines.flatMap((orderLine) => orderLine.appliedPromotions)
    ];
  }

  getShoppingCartStore() {
    return this.$injector.get('ShoppingCartStore');
  }

  getAddVerifiedSubjectService() {
    return this.$injector.get('AddVerifiedSubjectService');
  }
}

PersistentCartService.$inject = [
  '$http',
  '$q',
  '$window',
  '$injector',
  'JobStore',
  'SubjectStore',
  'OrderLineFactory',
  'LooksStore',
  'ProductCatalogStore',
  'ItemService',
  'EventDataService',
  'ErrorLogService',
  'StatsdService',
  'StorageService',
  'DeserializeAction',
  'SessionTimeoutAction',
  'LogoutSuccessAction',
  'PersistentCartPreprocessorService',
  'EnhancementStore'
];
App.service('PersistentCartService', PersistentCartService);
